/**
 * Company Details Hooks
 * React Query hooks for managing company details API operations
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import config from "@/lib/config";
import type { UpdateCompanyDetails } from "../types/company-details.types";

const QUERY_KEY = ["company-details"];

/**
 * Helper function for authenticated API requests
 */
async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem("auth-data");

  if (!token) {
    throw new Error("No authentication token found");
  }

  const response = await fetch(`${config.apiUrl}${url}`, {
    ...options,
    credentials: "include",
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("API Error:", response.status, errorText);
    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
  }

  return response.json();
}

/**
 * Fetch company details for current user
 */
export function useCompanyDetails() {
  const token = localStorage.getItem("auth-data");
  
  console.log("[useCompanyDetails] Token exists:", !!token);
  console.log("[useCompanyDetails] Query enabled:", !!token);
  
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      console.log("[useCompanyDetails] Fetching company details...");
      const result = await fetchWithAuth("/api/company-details");
      console.log("[useCompanyDetails] Result:", result);
      return result;
    },
    enabled: !!token, // Only run query if token exists
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true, // Always refetch when component mounts
  });
}

/**
 * Update company details
 */
export function useUpdateCompanyDetails() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateCompanyDetails) => {
      return fetchWithAuth("/api/company-details", {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
    },
  });
}
